import React, {
  Ref,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ImageBackground,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import {Button, ProgressBar, Text} from 'react-native-paper';
import {useMutation, useQuery} from 'react-query';
import {
  // CBProgressParams,
  downloadFileById,
  getActiveProjectLayer,
  getProjectMedia,
} from '../../../api/media';
import {useAppDispatch, useAppSelector} from '../../../store/hooks';
import {useIsFocused} from '@react-navigation/native';
import {
  setEncoderProgress,
  setCaretPosition,
  setScrollPosition,
  setIsPlaying,
  setActiveLayer,
} from '../../../store/slices/editor';
import {ResizeMode, Video} from 'expo-av';
import {EditorLayer, EditorProject, Media} from '../../../types';
import {setDownloadingProgress} from '../../../store/slices/fs';
import {EditorLoader} from './editorloader';
import {CBProgressParams} from '../../../api/encoder/ffmpeg';
const styles = StyleSheet.create({
  video: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  chip: {
    fontSize: 18,
  },
});

function EditorVideoComponent({
  video2,
  mediaRes,
  layers,
  projectId,
  height,
}: {
  projectId: string;
  layers?: EditorLayer[];
  mediaRes: EditorProject;
  video2: RefObject<JSX.IntrinsicElements['video'] | undefined>;
  height: number;
}) {
  const audio2 = useRef();
  const dispatch = useAppDispatch();
  const encoderProgress = useAppSelector(
    state => state.editor.encoderProgress,
  );
  const pixelsPerSecond = useAppSelector(
    state => state.editor.pixelsPerSecond,
  );
  const caretPosition = useAppSelector(
    state => state.editor.videoCaretPosition,
  );
  const scrollPosition = useAppSelector(
    state => state.editor.videoScrollPosition,
  );
  const video3 = useRef<Video | null>();
  const isPlaying = useAppSelector(state => state.editor.isPlaying);
  const canScroll = useAppSelector(state => state.editor.canScroll);
  const scroll = useAppSelector(state => state.editor.scroll);
  const activeLayer = useAppSelector(
    state => state.editor.activeLayer,
  );
  const focused = useIsFocused();
  const currentPosition =
    (caretPosition + scrollPosition) / pixelsPerSecond || 0.0;
  const scrollTimeoutQuery = useQuery(
    ['scrollTimeout_', projectId, scrollPosition],
    async () => {
      return await new Promise(res => {
        setTimeout(() => {
          res(1);
        }, 100);
      });
    },
  );
  const encodedMediaQuery = useQuery(
    ['encodedMedia', projectId, currentPosition, activeLayer],
    async () =>
      await getProjectMedia(
        layers!,
        currentPosition,
        (progress: CBProgressParams) =>
          dispatch(setEncoderProgress(progress.progress)),
      ),
    {
      enabled:
        mediaRes?.id !== undefined &&
        layers?.length &&
        focused &&
        !scrollTimeoutQuery.isFetching
          ? true
          : false,
      keepPreviousData: false,
      onSuccess: () => {
        console.info('after query');
        if (video2?.current)
          video2.current.currentPosition =
            encodedMediaQuery.data?.currentPosition || 0.0;
      },
    },
  );

  const queryPosition =
    encodedMediaQuery.data?.currentPosition || 0.0;
  const offsetPosition = encodedMediaQuery.data?.offset || 0.0;
  const {width} = useWindowDimensions();
  const onTimeUpdate = useCallback(
    (currentTime: number, paused: boolean) => {
      try {
        const newtime =
          (offsetPosition || 0) +
          currentTime -
          (encodedMediaQuery.data?.activeMedia.encoding.start || 0);

        if (newtime >= encodedMediaQuery.data?.totalTime) {
          if (Platform.OS === 'web') video2.current?.pause();
          else video3.current?.setStatusAsync({shouldPlay: false});
          dispatch(setIsPlaying(false));
        }

        const newoffset = newtime * pixelsPerSecond;
        const activeLayerId = getActiveProjectLayer(layers!, newtime)
          ?.activeMedia?.id;
        const layerSwitched =
          activeLayerId && activeLayerId !== activeLayer;
        let nscrollPosition = 0;
        let ncaretPosition = caretPosition;

        if (!paused) {
          if (canScroll) {
            const offset = newoffset - scroll;
            console.info('offset', offset, scroll, newoffset);
            if (offset > 0) {
              const diff = newoffset - offset;
              console.info('diff', diff, offset, scroll, newoffset);
              nscrollPosition = diff;
              ncaretPosition = offset;
            } else {
              nscrollPosition = newoffset - caretPosition;
            }
          } else {
            ncaretPosition = newoffset;
          }
          dispatch(setIsPlaying(true));
          dispatch(setCaretPosition(ncaretPosition));
          dispatch(setScrollPosition(nscrollPosition));
        }
        if (paused && isPlaying && Platform.OS === 'web')
          dispatch(setIsPlaying(false));
        if (layerSwitched) {
          // console.info('layerSwitched', layerSwitched, activeLayer, activeLayerId)
          dispatch(
            setActiveLayer({
              activeLayerId,
              scrollPosition: nscrollPosition,
              caretPosition: ncaretPosition,
            }),
          );
        }
      } catch (err) {
        console.error('ontimeupdate rr', err);
      }
    },
    [encodedMediaQuery, canScroll, isPlaying, layers, offsetPosition],
  );
  // console.info('encoded', encodedMediaQuery.data);
  const platformWidth = Platform.select({
    web: 'auto',
    ios: width,
    android: width,
  });
  const statusMessageStyle: ViewStyle = {
    display: 'flex',
    width: platformWidth,
    height,
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'center',
    gap: 20,
    paddingTop: height / 2 - 50,
  };
  let fileStatusMessage = 'Media is missing';
  const isAudioMissing =
    encodedMediaQuery.data?.audioUrl === 'file_is_not_downloaded';
  const isVideoMissing =
    encodedMediaQuery?.data?.url === 'file_is_not_downloaded';
  if (isVideoMissing || isAudioMissing) {
    return (
      <EditorLoader
        width={width}
        height={height}
        queryData={encodedMediaQuery?.data}
        isVideoMissing={isVideoMissing}
        isAudioMissing={isAudioMissing}
        refetch={encodedMediaQuery.refetch}
      />
    );
  }
  if (
    (encoderProgress && encoderProgress !== 1) ||
    encodedMediaQuery.isFetching
  ) {
    fileStatusMessage = 'Prerendering';
    return (
      <View style={{...statusMessageStyle, padding: 0}}>
        <Text
          style={{
            position: 'absolute',
            top: height / 2 - 50,
            color: 'black',
            zIndex: 10,
          }}>
          {fileStatusMessage}
        </Text>
        <ProgressBar
          style={{
            width: platformWidth,
            height: height - 50,
            top: 0,
          }}
          progress={encoderProgress}
        />
      </View>
    );
  }
  const separateAudio =
    encodedMediaQuery?.data?.audioLayer?.activeMedia;
  return (
    <View style={{height, width: '100%'}}>
      {Platform.OS === 'web' ? (
        <video
          controls={false}
          style={styles.video}
          src={(encodedMediaQuery.data?.url as string) || ''}
          muted={encodedMediaQuery.data?.muted}
          ref={e => {
            // console.info('rerender video')
            // console.info("ref", e, encodedMediaQuery.data)
            if (e) {
              e.currentTime = queryPosition;
              video2.current = e;
              if (isPlaying) e.play();
            }
          }}
          onPlaying={e => {
            dispatch(setIsPlaying(true));
          }}
          onPause={e => {
            dispatch(setIsPlaying(false));
          }}
          onEnded={p => {
            console.info('ended', p);
          }}
          onTimeUpdate={e =>
            onTimeUpdate(
              (e.nativeEvent.target as JSX.IntrinsicElements['video'])
                .currentTime,
              (e.nativeEvent.target as JSX.IntrinsicElements['video'])
                .paused,
            )
          }
        />
      ) : (
        <Video
          source={{
            uri: (encodedMediaQuery.data?.url as string) || '',
          }}
          positionMillis={parseInt(queryPosition * 1000)}
          shouldPlay={isPlaying}
          ref={e => {
            // console.info('onref', e);
            video3.current = e;
            if (isPlaying) e?.setStatusAsync({shouldPlay: true});
          }}
          isMuted={encodedMediaQuery.data?.muted}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: 0,
            bottom: 0,
            padding: 0,
            margin: 0,
          }}
          volume={1}
          resizeMode={ResizeMode.CONTAIN}
          useNativeControls={false}
          onPlaybackStatusUpdate={async status => {
            if (status.isLoaded && status.positionMillis) {
              onTimeUpdate(
                status.positionMillis / 1000,
                !status.isPlaying,
              );
            }
          }}
        />
      )}
      {separateAudio &&
        encodedMediaQuery?.data?.activeMedia.id !==
          separateAudio.id && (
          <>
            {Platform.OS === 'web' ? (
              <video
                src={encodedMediaQuery.data?.audioUrl || ''}
                style={{opacity: 0, width: 0, height: 0}}
                volume={1}
                controls={false}
                muted={false}
                ref={e => {
                  console.info('rerender audio', e);
                  console.info('ref', e, encodedMediaQuery.data);
                  if (e) {
                    e.currentTime =
                      encodedMediaQuery.data?.audioPosition;
                    audio2.current = e;
                    if (isPlaying) e.play();
                  }
                }}
              />
            ) : (
              <Video
                source={{
                  uri:
                    (encodedMediaQuery.data?.audioUrl as string) ||
                    '',
                }}
                shouldPlay={isPlaying}
                isMuted={false}
                volume={1}
                positionMillis={
                  encodedMediaQuery.data?.audioPosition * 1000
                }
                // style={{display:"none"}}
              />
            )}
          </>
        )}
    </View>
  );
}
export default EditorVideoComponent;
